<template>
	<div class="leaderboard-view">
		<!-- Leaderboard -->
		<template v-if="showLeaderboard">
			<template v-if="results.length">
				<LeaderboardGrid
					v-if="type === types.grid"
					:results="results"
					@view="onView"
				/>
				<Leaderboard v-else :results="results" @view="onView" />
			</template>
			<div v-else class="leaderboard-view__empty">
				<h2>Nothing has made it to the leaderboard just yet.</h2>
			</div>
			<ButtonGroup>
				<SpinnerButton
					v-if="!fetchedAll"
					class="btn"
					:loading="loading"
					@click.native="() => getLeaderboard()"
					>See more</SpinnerButton
				>
			</ButtonGroup>
			<div class="leaderboard-view__buttons">
				<StateButtons :exclude="['leaderboard']" />
			</div>
		</template>
		<!-- Loading -->
		<div v-else class="leaderboard-view__loading">
			<Loading color="white" />
		</div>
		<!-- Submission view overlay -->
		<portal>
			<transition name="fade">
				<PolaroidView
					v-if="viewing"
					darken
					:submission="viewing"
					@dismiss="dismiss"
				/>
			</transition>
		</portal>
	</div>
</template>

<script>
import { enums } from 'protoswipe-shared'
import { mapState, mapGetters, mapActions } from 'vuex'
import toast from '@/mixins/toast'
import Loading from '@/components/shared/Loading'
import StateButtons from '@/components/main/StateButtons'
import SpinnerButton from '@/components/main/SpinnerButton'
import Leaderboard from '@/components/main/Leaderboard'
import LeaderboardGrid from '@/components/main/LeaderboardGrid'
import ButtonGroup from '@/components/main/ButtonGroup'
import PolaroidView from '@/components/main/PolaroidView'

const { LeaderboardType } = enums

export default {
	name: 'LeaderboardView',
	data() {
		return {
			viewingId: null,
			types: LeaderboardType
		}
	},
	computed: {
		...mapState({
			type: s => s.campaign.campaign.leaderboardType || LeaderboardType.list,
			loading: s => s.leaderboard.loading,
			fetchedAll: s => s.leaderboard.done
		}),
		...mapGetters({
			loadedAny: 'leaderboard/loadedAny',
			results: 'leaderboard/results'
		}),
		showLeaderboard() {
			return this.loadedAny
		},
		viewing() {
			return this.results.find(r => r.id === this.viewingId)
		}
	},
	methods: {
		...mapActions({
			fetchLeaderboard: 'leaderboard/fetch'
		}),
		async getLeaderboard(force) {
			if (this.loading) return

			const opts = { force, type: this.type }
			const { success, error } = await this.fetchLeaderboard(opts)
			if (!success) {
				// On fail, push back to homepage and show toast
				if (error) {
					switch (error.status) {
						case 403:
							this.toastWarn(error.message)
							break
						default:
							this.toastErr(error.message)
							break
					}
				}
				this.$router.push({ name: 'Home' })
			}
		},
		onView(id) {
			this.viewingId = id
		},
		dismiss() {
			this.viewingId = null
		}
	},
	created() {
		// Force-fetch leaderboard
		this.getLeaderboard(true)
	},
	mixins: [toast],
	components: {
		Loading,
		StateButtons,
		SpinnerButton,
		Leaderboard,
		LeaderboardGrid,
		ButtonGroup,
		PolaroidView
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/layout';
@import '~scss/components';
@import '~scss/mq';
@import '~scss/transitions';

@include fade($spd-wall);

.leaderboard-view {
	margin-bottom: 2rem;
	padding: 0 1rem;

	&__loading {
		position: relative;
		height: $loading-h;
	}

	&__empty {
		color: $white;
		display: flex;
		text-align: center;
		align-items: center;
		justify-content: center;
	}

	&__submission {
		@extend %abs-full;
		position: fixed;
		background-color: rgba($black, 0.75);
	}

	@include mq(lg) {
		&__buttons {
			margin-top: 4rem;
			margin-bottom: 4rem;
		}
	}
}

.btn {
	@include button($blue-48, $white, $blue-48, $blue-98);
}
</style>
